import styled from '@emotion/styled';
import { SyntheticEvent } from 'react';
import { switchProp } from 'styled-tools';
import {
    StyledSimpleProductInfoTitle,
    StyledSimpleProductInfoBrand,
    StyledSimpleProductInfoMaterial,
} from './styled';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useWishlist } from '~/features/commerce-api/hooks/wishlist/useWishList';
import { TranslationKey } from '~/lib/data-contract';
import { isDispatchableProductEvent } from '~/lib/salesforce/types';
import { Image } from '~/shared/components';
import { EventIdentifier, useEvents } from '~/shared/hooks/useEvents';
import { _transformProductDetailsToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { useUtils } from '~/shared/hooks/useUtils';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { AspectRatio } from '../AspectRatio';
import Button, { StyledButton } from '../Button';
import Flex from '../Flex';
import { Grid } from '../Grid/Grid';
import { TProductDetails } from '../ProductCard/ProductCard.definition';
import { FavWrapper, Favourite } from '../ProductCard/components/Favourite';
import { Price, PriceLabel, TPrice } from '../ProductCard/components/Price';
import { ProductCardLink } from '../ProductCard/components/ProductCardLink';
import { toBoolean } from '~/shared/utils/toBoolean';
import { truncateText } from '~/shared/utils/truncate-text';

export const SimpleProductCard = styled(
    (
        product: TProductDetails & {
            listId: EventIdentifier;
            listName: EventIdentifier;
            className?: string;
            displaySize?: 'small' | 'large';
            position: number;
            raptorAlgorithm?: string;
            isMiniBasket: boolean;
            header?: string;
        },
    ) => {
        const { handleFavourite, isFavorite } = useWishlist();
        const { translate } = useTranslation();
        const { data: frame } = useFrame();
        const { productClickEvent } = useEvents(frame);
        const { isPlusMember } = useCustomer();
        const {
            id,
            brand,
            label,
            longName,
            url,
            className,
            onBuyBtn,
            cust_in_stock,
            listId,
            listName,
            position,
            ctaButtonProductCard,
            raptorAlgorithm,
            isMiniBasket,
            header,
        } = product;
        const prices = product.c_prices ?? product.cust_prices;

        const summerImage = product.images && product.images[0];

        const productEventData = _transformProductDetailsToDataLayer(
            product,
            1,
            position,
            isPlusMember,
        );

        const handleBuyBtn = (e: SyntheticEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const eventType = ctaButtonProductCard.translationKey;

            if (onBuyBtn && isDispatchableProductEvent(eventType)) {
                const payload = {
                    productId: id,
                    orderable: cust_in_stock,
                    quantity: 1,
                    url,
                };
                onBuyBtn(listId, {
                    type: eventType,
                    productEventData,
                    payload,
                    quantity: 1,
                });
            }
        };

        const onProductClick = () => {
            productClickEvent(listId, `${listName} - ${header}`, productEventData, raptorAlgorithm);
        };

        return (
            <ProductCardLink url={url} className={className} onClick={onProductClick}>
                <Grid flow="column" columns={'128px 1fr'}>
                    <div>
                        {summerImage && (
                            <ImageWrap>
                                <AspectRatio ratio="1/1">
                                    <Image src={summerImage.src} sizes="96px" objectFit="cover" />
                                </AspectRatio>
                            </ImageWrap>
                        )}
                        <Favourite
                            isFavourite={isFavorite(id)}
                            onFavourite={() => handleFavourite(product)}
                        />
                    </div>

                    <InfoWrap>
                        <Flex justifyContent="space-between">
                            <div style={{ width: '50%' }}>
                                <StyledSimpleProductInfoBrand isMiniBasket={isMiniBasket}>
                                    {brand}
                                </StyledSimpleProductInfoBrand>
                                <StyledSimpleProductInfoTitle isMiniBasket={isMiniBasket}>
                                    {isMiniBasket ? truncateText(longName ?? '', 26) : longName}
                                </StyledSimpleProductInfoTitle>
                                <StyledSimpleProductInfoMaterial isMiniBasket={isMiniBasket}>
                                    {isMiniBasket ? label : ''}
                                </StyledSimpleProductInfoMaterial>
                            </div>

                            {toBoolean(ctaButtonProductCard?.shouldRender) && (
                                <div>
                                    <Button
                                        small
                                        size="small"
                                        disabled={toBoolean(ctaButtonProductCard.isDisabled)}
                                        children={translate(
                                            `plp.${ctaButtonProductCard.translationKey}` as TranslationKey,
                                        )}
                                        onClick={handleBuyBtn}
                                    />
                                </div>
                            )}
                        </Flex>
                        {prices && <Prices prices={prices} />}
                    </InfoWrap>
                </Grid>
            </ProductCardLink>
        );
    },
)(
    ({ theme }) => ({
        background: theme.colors.white,
        borderBottom: `1px solid ${theme.colors.brownLight}`,

        [`${StyledButton}`]: {
            fontWeight: theme.mixins.useFontWeight('Bold'),
        },

        [`${FavWrapper}`]: {
            right: 'auto',
            left: 0,
            padding: theme?.space[2],
            svg: {
                margin: 0,
            },
        },
    }),

    switchProp('displaySize', {
        small: ({ theme }) => ({
            [`${StyledSimpleProductInfoBrand}`]: {
                fontSize: theme.fontSizes['exs'],
            },
            [`${StyledSimpleProductInfoTitle}`]: {
                fontSize: theme.fontSizes['2xs'],
                lineHeight: theme.lineHeights.quarter,
                fontWeight: theme.mixins.useFontWeight('Bold'),
            },
            [`${StyledSimpleProductInfoMaterial}`]: {
                fontSize: theme.fontSizes['exs'],
            },

            [`${PriceLabel}`]: {
                fontSize: theme.fontSizes['2xs'],
                lineHeight: theme.lineHeights.base,
            },
            [`${Price}`]: {
                fontSize: theme.fontSizes['2xs'],
                lineHeight: theme.lineHeights.base,
            },
        }),
        large: ({ theme }) => ({
            [`${StyledSimpleProductInfoTitle}`]: {
                fontSize: theme.fontSizes['md'],
            },
            [`${StyledSimpleProductInfoBrand}`]: {
                fontSize: theme.fontSizes['sm'],
            },
            [`${StyledSimpleProductInfoMaterial}`]: {
                fontSize: theme.fontSizes['xs'],
            },
            [`${PriceLabel}`]: {
                fontSize: theme.fontSizes['xs'],
                lineHeight: theme.lineHeights.base,
                paddingLeft: '50%',
            },
            [`${Price}`]: {
                fontSize: theme.fontSizes['xs'],
                lineHeight: theme.lineHeights.base,
            },
        }),
    }),
);

const ImageWrap = styled.div(({ theme }) => ({
    background: theme.colors.productImageBackround,
    padding: theme.space[3],
    img: {
        mixBlendMode: 'multiply',
    },
}));

const InfoWrap = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.space[3],
}));

const Prices = styled(({ className, prices }: { className?: string; prices: TPrice[] }) => {
    const { convertPriceToLocale } = useUtils();

    // Reverse the array before mapping over it
    const reversedPrices = [...prices].reverse();

    return (
        <div className={className}>
            {reversedPrices.map((p, index) => (
                <Flex justifyContent="flex-end" key={`${p.value}-${index.toString()}`}>
                    <PriceLabel style={{ marginRight: '10px' }} type={p.type}>
                        {p.label}
                    </PriceLabel>
                    <Price type={p.type}>{convertPriceToLocale(p.value)}</Price>
                </Flex>
            ))}
        </div>
    );
})(() => ({}));
